import { useMemo, useState } from 'react'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import styles from 'components/charts/Charts.module.scss'
import ScrollableBarChart from 'components/charts/scrollable-bar-chart/ScrollableBarChart.tsx'
import Legend from 'components/charts/legend/Legend.tsx'
import { formatAmount } from 'utils/format.ts'
import { getSalesDashboardChartDatasets, getSalesDashboardChartOptions } from './chartjs.config.ts'
import { BAR_SLOT_WIDTH } from './constants.ts'
import { SalesDashboardChartProps } from './types.ts'

const SalesDashboardChart = ({
  data,
  series,
  legendTitle,
  xAxisLabel,
  yAxisLabel,
  label,
  loading,
  barSlotWidth = BAR_SLOT_WIDTH
}: SalesDashboardChartProps) => {
  // Local state
  const [activeDataPointIndex, setActiveDataPointIndex] = useState<number | null>(null)

  // Memoized constants
  const labels = useMemo(() => data.map((entry) => entry.label), [data])
  const options = useMemo(
    () =>
      getSalesDashboardChartOptions({
        labels,
        yAxisLabel,
        handleHoverDataPoint: setActiveDataPointIndex
      }) as any,
    [labels, yAxisLabel]
  )
  const chartData = useMemo(
    () =>
      ({
        labels,
        datasets: getSalesDashboardChartDatasets({ labels, data, series })
      }) as any,
    [labels, data, series]
  )

  // Constants
  const activeEntry = typeof activeDataPointIndex === 'number' ? data[activeDataPointIndex] : undefined

  if (loading) {
    return (
      <LoadingSkeleton>
        <LoadingSkeleton.Rectangle aspectRatio="2/1" />
      </LoadingSkeleton>
    )
  }

  return (
    <>
      <div className={styles['chart-wrapper']}>
        <ScrollableBarChart options={options} data={chartData} label={label} xAxisLabel={xAxisLabel} barSlotWidth={barSlotWidth} />
      </div>

      <Legend title={activeEntry ? activeEntry.hoverLabel || activeEntry.label : legendTitle}>
        {series.map(({ key, label: seriesLabel, color }) => (
          <Legend.Entry key={key} label={seriesLabel} indicator={{ color, full: true }}>
            {activeEntry ? (
              <data value={activeEntry.values[key]}>{formatAmount(activeEntry.values[key], { fractionDigits: 0 })}</data>
            ) : (
              ' '
            )}
          </Legend.Entry>
        ))}
      </Legend>
    </>
  )
}

export default SalesDashboardChart
